/* ==========================================================================
   Newspaper Video & Offer — pause the video when it scrolls out of the
   viewport, and resume it when it scrolls back in.

   Used by sections/newspaper-video-offer.liquid.

   Only playback that THIS script paused is resumed. If the visitor pauses
   the video themselves, scrolling away and back leaves it paused — we never
   restart something they deliberately stopped.

   Handles the three media types the section can render:
     - native <video> (Shopify-hosted video)
     - YouTube iframe (postMessage, needs enablejsapi=1 on the src)
     - Vimeo iframe (postMessage)
 
   Note on autoplay: browsers only allow autoplay while the video is muted,
   which is why the section ties `muted` to the Autoplay setting. A resume
   call on an unmuted video can still be refused if the visitor has not yet
   interacted with the page; the rejection is swallowed and the video simply
   stays paused.
   ========================================================================== */ 

(function () {
  'use strict';

  var VISIBLE_RATIO = 0.25;
  var states = new WeakMap();

  function getState(container) {
    var state = states.get(container);
    if (!state) {
      state = { pausedByScript: false, userPaused: false, selfPausing: false };
      states.set(container, state);
    }
    return state;
  }

  function postToFrame(iframe, message) {
    if (!iframe || !iframe.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(JSON.stringify(message), '*');
    } catch (e) {
      /* Cross-origin frame not ready — nothing breaks. */
    }
  }

  function frameCommand(container, action) {
    var iframe = container.querySelector('iframe');
    if (!iframe) return false;

    var src = iframe.src || '';

    if (src.indexOf('youtube.com') !== -1) {
      postToFrame(iframe, {
        event: 'command',
        func: action === 'play' ? 'playVideo' : 'pauseVideo',
        args: []
      });
      return true;
    }

    if (src.indexOf('vimeo.com') !== -1) {
      postToFrame(iframe, { method: action === 'play' ? 'play' : 'pause' });
      return true;
    }

    return false;
  }

  function pauseMedia(container) {
    var state = getState(container);
    var video = container.querySelector('video');

    if (video) {
      if (video.paused) return;
      // The 'pause' event fires asynchronously, so this flag must stay set
      // until the listener below consumes it — clearing it here would make
      // our own pause look like a visitor-initiated one.
      state.selfPausing = true;
      video.pause();
      state.pausedByScript = true;
      return;
    }

    if (frameCommand(container, 'pause')) state.pausedByScript = true;
  }

  function resumeMedia(container) {
    var state = getState(container);
    if (!state.pausedByScript || state.userPaused) return;

    var video = container.querySelector('video');

    if (video) {
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () {
          /* Autoplay policy refused the resume — leave it paused. */
        });
      }
      state.pausedByScript = false;
      return;
    }

    if (frameCommand(container, 'play')) state.pausedByScript = false;
  }

  function trackUserIntent(container) {
    var video = container.querySelector('video');
    if (!video) return;

    var state = getState(container);

    video.addEventListener('pause', function () {
      // Our own pause: consume the flag and leave userPaused alone.
      if (state.selfPausing) {
        state.selfPausing = false;
        return;
      }
      // Any other pause means the visitor stopped it themselves.
      state.userPaused = true;
    });

    video.addEventListener('play', function () {
      state.userPaused = false;
    });
  }

  function observe(container) {
    if (container.dataset.viewportPlaybackBound === 'true') return;
    container.dataset.viewportPlaybackBound = 'true';

    trackUserIntent(container);

    // No IntersectionObserver (very old browsers): leave playback alone
    // rather than shipping a scroll-handler fallback.
    if (typeof IntersectionObserver === 'undefined') return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var visible =
            entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO;

          if (visible) {
            resumeMedia(entry.target);
          } else {
            pauseMedia(entry.target);
          }
        });
      },
      { threshold: [0, VISIBLE_RATIO] }
    );

    observer.observe(container);
  }

  function init(root) {
    (root || document)
      .querySelectorAll('.newspaper-video-offer__media')
      .forEach(observe);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Re-bind after a theme editor section reload.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // Pause when the section is deselected in the theme editor.
  document.addEventListener('shopify:section:deselect', function (event) {
    event.target
      .querySelectorAll('.newspaper-video-offer__media')
      .forEach(pauseMedia);
  });

  // Pause when the tab itself is hidden.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'hidden') return;
    document.querySelectorAll('.newspaper-video-offer__media').forEach(pauseMedia);
  });
})();
